import { Emitter, Event } from "../../base/common/events.js";
import { Disposable } from "../../base/common/lifecycle.js";

export interface AttachmentChangedEvent {
    readonly attachment: IAttachment;

    readonly type?: string;
    readonly visible?: boolean;
    readonly enabled?: boolean;
    readonly label?: string;
    readonly description?: string;
    readonly icon?: string;
}

export interface IAttachment {
    readonly onDidChange: Event<AttachmentChangedEvent>;

    readonly type: string;
    /** When false the attachment should not be visible in the UI */
    visible: boolean;
    /** When false the attachment should not be interactable (e.g. disabled in the UI) */
    enabled: boolean;

    /** The name is a short descriptive identifier, it can be used for telemetry to describe the target of the object. The name should not be displayed in the UI */
    name?: string;
    label?: string;
    description?: string;
    icon?: string;
    /** Indicator if the attachment is currently active. */
    active: boolean;

    run?(): void;
}
export interface IActivityAttachment extends Omit<IAttachment, "run"> {
    readonly type: 'activity';
}



export class Attachment extends Disposable implements IAttachment {
    protected readonly _onDidChange = new Emitter<AttachmentChangedEvent>();
    readonly onDidChange = this._onDidChange.event;

    readonly type: string;

    protected _visible: boolean = true;
    get visible(): boolean {   
        return this._visible;
    }
    set visible(value: boolean) {
        if (this._visible !== value) {
            this._visible = value;
            this._onDidChange.fire({ attachment: this, visible: value } );
        }
    }

    protected _enabled: boolean;
    get enabled(): boolean {
        return this._enabled;
    }
    set enabled(value: boolean) {
        if (this._enabled !== value) {
            this._enabled = value;
            this._onDidChange.fire({ attachment: this, enabled: value } );
        }
    }

    protected _active: boolean = true;
    get active() {
        return this._active;
    }
    set active(value: boolean) {
        if (this._active !== value) {
            this._active = value;
            this._onDidChange.fire({ attachment: this } );
        }
    }

    protected _icon?: string | undefined;
    get icon(): string | undefined {
        return this._icon;
    }
    set icon(value: string | undefined) {
        if (this._icon !== value) {
            this._icon = value;
            this._onDidChange.fire({ attachment: this, icon: value } );
        }
    }

    protected _label?: string | undefined;
    get label(): string | undefined {
        return this._label;
    }
    set label(value: string | undefined) {
        if (this._label !== value) {
            this._label = value;
            this._onDidChange.fire({ attachment: this, label: value } );
        }
    }

    protected _name: string | undefined;
    get name(): string | undefined {
        return this._name;
    }
    set name(value: string | undefined) {
        if (this._name !== value) {
            this._name = value;
            this._onDidChange.fire({ attachment: this } );
        }
    }

    
    protected _description?: string;
    get description(): string | undefined {
        return this._description;
    }
    set description(value: string | undefined) {
        if (this._description !== value) {
            this._description = value;
            this._onDidChange.fire({ attachment: this, description: value } );
        }
    }

    constructor(
        type: string,
        label?: string,
        description?: string,
        icon?: string,
        run?: () => void
    ) {
        super();
        this.type = type;
        this.label = label;
        this.description = description;
        this._icon = icon;
        this.enabled = true;
        if(run) this.run = run;
    }

    run?(): void;
}
